import { useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import Hls from 'hls.js';
import React, { useEffect, useRef, useState } from 'react';
import { CommentsForPostResponse, postsApi } from 'src/api/posts';
import dunbarWhite from 'src/assets/images/dunbar/dunbar-white.png';
import { CommentsSide } from 'src/pages/ChallengePage/CommentsSide/CommentsSide';
import { SelectedVideoModal } from 'src/pages/UserCircle/Tabs/Videos/SelectedVideoModal/SelectedVideoModal';
import { useVideoEngagement } from 'src/utils/videoEngagement';
import styles from './VideoPlayer.module.scss';

interface VideoPlayerProps {
  videoUrl?: string;
  post: any;
  index: number;
  active: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  post,
  index,
  active,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [browser, setBrowser] = useState('');
  const isTablet = useMediaQuery('(max-width:825px)');

  const { createHandlers } = useVideoEngagement({
    postId: post._id ?? '',
  });

  const [commentsData, setCommentsData] = useState<CommentsForPostResponse>({
    result: [],
    children: [],
    pagination: { currentPage: 1, pageSize: 10 },
  });
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentsCount, setCommentsCount] = useState(post.comments.length);
  const fetchedPosts = useRef<Set<string>>(new Set());
  const isMobile = useMediaQuery('(max-width:825px)');

  const displayedChallenges = post.challenges.slice(0, 2);
  const moreChallengesCount =
    post.challenges.length - displayedChallenges.length;

  const truncateText = (text: string, maxLength: number) =>
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

  const calculateRelativeTime = (date: string) => {
    const createdAt = new Date(date);
    return `${createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    })}, ${createdAt.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`;
  };

  const relativeTime = calculateRelativeTime(post.createdAt);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const vendor = navigator.vendor.toLowerCase();

    if (vendor.includes('google inc.')) {
      setBrowser('Chrome');
    } else if (
      vendor.includes('apple') &&
      userAgent.includes('safari') &&
      !userAgent.includes('chrome')
    ) {
      setBrowser('Safari');
    } else {
      setBrowser('Other');
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) {
      return;
    }

    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handleLoadedData = () => setIsLoading(false);

    video.oncanplay = handleCanPlay;
    video.onwaiting = handleWaiting;
    video.onloadeddata = handleLoadedData;

    return () => {
      if (video) {
        video.oncanplay = null;
        video.onwaiting = null;
        video.onloadeddata = null;
      }
    };
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) {
      return;
    }

    if (active) {
      video.play().catch((err) => console.error('Failed to play video:', err));
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [active, videoUrl]);

  // Set up video engagement handlers
  useEffect(() => {
    if (post && videoRef.current && active) {
      const { handleTimeUpdate, handleEnded } = createHandlers(videoRef);
      const video = videoRef.current;

      // Handle manual looping after completion tracking
      const handleEndedWithLoop = () => {
        // First, track the completion
        handleEnded();

        // Then restart the video to simulate loop behavior
        if (active) {
          video.currentTime = 0;
          video
            .play()
            .catch((err) => console.error('Failed to restart video:', err));
        }
      };

      video.ontimeupdate = handleTimeUpdate;
      video.onended = handleEndedWithLoop;

      return () => {
        if (video) {
          video.ontimeupdate = null;
          video.onended = null;
        }
      };
    }
  }, [post, createHandlers, active]);

  useEffect(() => {
    if (!active && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [active, post._id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) {
      return;
    }

    const handleLoadedMetadata = () => {
      setIsLandscape(video.videoWidth > video.videoHeight);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoUrl]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoUrl) {
      return;
    }

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoUrl);
      hls.attachMedia(videoElement);
      return () => hls.destroy();
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = videoUrl;
    }
  }, [videoUrl]);

  useEffect(() => {
    if (isOpenModal && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isOpenModal]);

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleDunbarClick = () => {
    setIsOpenModal(true);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  useEffect(() => {
    if (active && post._id && !fetchedPosts.current.has(post._id)) {
      setLoadingComments(true);
      postsApi
        .getCommentsForPost(post._id)
        .then((res) => {
          setCommentsData(res);
          fetchedPosts.current.add(post._id);
        })
        .catch((err) => {
          console.error('Failed to fetch comments for video:', err);
        })
        .finally(() => {
          setLoadingComments(false);
        });
    }
  }, [active, post._id]);

  return (
    <div className={styles.container}>
      <div className={styles.videoContainer}>
        <div
          className={classNames(styles.videoWrapper, {
            [styles.videoWrapperGoogle]: browser === 'Chrome',
            [styles.videoWrapperSafari]: browser === 'Safari',
            [styles.isTablet]: isTablet,
          })}
        >
          {isLoading && videoUrl && (
            <div className={styles.loaderOverlay}>
              <div className={styles.loaderSpinner}></div>
            </div>
          )}

          {videoUrl ? (
            <video
              className={classNames(styles.video, {
                [styles.landscapeVideo]: isLandscape,
              })}
              ref={videoRef}
              onClick={handleVideoClick}
              controls={false}
              autoPlay={false}
              muted={false}
              data-index={index}
              playsInline
            />
          ) : (
            <div className={styles.videoUnavailable}>Video unavailable</div>
          )}

          <div className={styles.challengeOverlay}>
            <div className={styles.topData}>
              <div className={styles.circleName}>{post.tribe_name}</div>
              <div className={styles.time}>{relativeTime}</div>
            </div>
            <div className={styles.bottomTitle}>Actions:</div>
            {displayedChallenges.map((challenge: any, index: number) => (
              <div className={styles.challengeTitle} key={index}>
                {truncateText(challenge.name, 37)}
              </div>
            ))}
            {moreChallengesCount > 0 && (
              <div className={styles.moreItem}>+{moreChallengesCount} more</div>
            )}
            <div
              className={styles.dunbarIconWrapper}
              onClick={isMobile ? handleDunbarClick : undefined}
            >
              <img
                src={dunbarWhite}
                alt="Dunbar"
                className={styles.dunbarIcon}
              />
            </div>
            <div className={styles.commentsCount}>{commentsCount} comments</div>
          </div>
        </div>
      </div>

      {!isTablet && (
        <div className={styles.commentsSection}>
          <CommentsSide
            commentsData={commentsData}
            postId={post._id}
            loading={loadingComments}
            isFeed={true}
            commentsCount={commentsCount}
            setCommentsCount={setCommentsCount}
            challenges={post.challenges}
          />
        </div>
      )}

      <SelectedVideoModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        postId={post._id}
        videoUrl={post.video_url}
      />
    </div>
  );
};

export default VideoPlayer;
